import * as definitions from '@database/common-schema/definitions';
import { mergeSchemas, state, brand } from '@database/common-schema/definitions';
import { AssemblerParagraphSubdocumentSchema, AssemblerImageParagraphSubdocumentSchema, } from '../common/assembler.paragraph.block.schema.js';
import { PartnerTypeEnum } from '../common/ca.common.model.schema.js';
const LinkedContentSubdocumentSchema = mergeSchemas([
    definitions.brandableMixin,
    {
        type: 'object',
        title: 'ILinkedContentSubdocument',
        additionalProperties: false,
        schemaType: 'subdocument',
        properties: {
            contentUuid: definitions.uuid,
            isBought: { type: 'boolean', description: 'Is the content bought by the publisher, needed for tracking' },
            state: state,
            paths: {
                type: 'array',
                items: definitions.brandAwarePath,
                description: 'all paths of the article; latest path in array is recent one',
            },
        },
        required: ['contentUuid', 'isBought', 'state'],
    },
]);
// AUTOPUBLISH SUBDOCUMENT SCHEMA
export const AssemblerPublishOperationEnum = {
    title: 'IAssemblerPublishOperation',
    schemaType: 'enum',
    enum: ['insert', 'update'],
};
export const AssemblerPublishMessageTypeEnum = {
    title: 'IAssemblerPublishMessageType',
    schemaType: 'enum',
    enum: ['error', 'warning', 'info'],
};
export const AssemblerPublishStateEnum = {
    title: 'IAssemblerPublishState',
    schemaType: 'enum',
    enum: ['success', 'error'],
};
export const AssemblerPublishMessageSubdocumentSchema = {
    title: 'IAssemblerPublishMessageSubdocument',
    type: 'object',
    additionalProperties: false,
    properties: {
        message: {
            type: 'string',
            description: 'Description of an event during auto-publish workflow.',
        },
        date: definitions.dateOrString,
        type: AssemblerPublishMessageTypeEnum,
    },
    required: ['message', 'date', 'type'],
};
export const AssemblerPublishEventSubdocumentSchema = {
    title: 'IAssemblerPublishEventSubdocument',
    type: 'object',
    additionalProperties: false,
    properties: {
        publishDate: definitions.dateOrString,
        operation: AssemblerPublishOperationEnum,
        messages: {
            type: 'array',
            items: AssemblerPublishMessageSubdocumentSchema,
        },
    },
    required: ['publishDate', 'operation', 'messages'],
};
export const AssemblerAutopublishSubdocumentSchema = {
    title: 'IAssemblerAutopublishSubdocument',
    type: 'object',
    additionalProperties: false,
    properties: {
        brand: definitions.brand,
        state: AssemblerPublishStateEnum,
        history: {
            type: 'array',
            items: AssemblerPublishEventSubdocumentSchema,
        },
    },
    required: ['brand', 'state', 'history'],
};
export const AssemblerLinkedPartnerSubdocumentSchema = {
    title: 'IAssemblerLinkedPartnerSubdocument',
    type: 'object',
    schemaType: 'block',
    additionalProperties: false,
    properties: {
        name: { type: 'string' },
        pNumberRevShare: { type: 'string' },
        pNumberFixum: { type: 'string' },
        isActiveInCtf: { type: 'boolean' },
        partnerType: PartnerTypeEnum,
        deviationDistribution: { type: 'string' },
    },
    required: ['name', 'pNumberRevShare', 'isActiveInCtf', 'partnerType'],
};
export const AssemblerCtfMetaSubdocumentSchema = {
    title: 'IAssemblerCtfMetaSubdocument',
    type: 'object',
    additionalProperties: false,
    properties: {
        isBought: {
            type: 'boolean',
            default: false,
            description: 'Indicates whether article has been bought in CTF.',
        },
        state: definitions.state,
    },
};
export const AssemblerContentSchema = {
    type: 'object',
    title: 'IAssemblerContent',
    schemaType: 'block',
    additionalProperties: true,
    properties: {
        _id: definitions.uuid,
        type: { enum: ['CONTENT', 'IMAGE'], default: 'CONTENT' },
        mainImage: {
            oneOf: [AssemblerImageParagraphSubdocumentSchema],
            description: 'main image for the content',
        },
        title: { type: 'string', description: 'Kicker: Title is headline' },
        seoTitle: { type: 'string', description: 'Used in title html tag' },
        kicker: { type: 'string', description: 'Kicker: Title are headline' },
        leadText: {
            type: 'string',
            description: 'Short description of article for teasering',
        },
        createDate: definitions.dateOrString,
        changeDate: definitions.dateOrString,
        publishDate: definitions.dateOrString,
        deleteDate: definitions.dateOrString,
        isVisibleInCtf: { type: 'boolean', default: true },
        teaserType: {
            enum: ['image', 'gallery', 'video'],
            description: 'defines if what teaser kind is used. Influences the teaser{type}Uuid selection',
        },
        content: {
            type: 'array',
            items: AssemblerParagraphSubdocumentSchema,
            entityMultiReference: {
                bindingProperty: 'paragraphs',
                typeIdentifier: 'type',
            },
        },
        canonicalUrl: {
            type: 'string',
            description: 'Canonical url of the article which can be set by the editor',
        },
        linkedContentsRef: {
            type: 'array',
            items: LinkedContentSubdocumentSchema,
        },
        allowedBrands: {
            type: 'array',
            items: brand,
        },
        feedUuid: {
            type: 'string',
            description: 'Feed ID (in CA DB) to allow filtering by feed',
        },
        ctfMeta: AssemblerCtfMetaSubdocumentSchema,
        partnerUuid: {
            type: ['string'],
            description: 'Partner id associated with content',
        },
        partner: AssemblerLinkedPartnerSubdocumentSchema,
        showPartnerInfo: {
            type: 'boolean',
            description: 'If true, will show partner logo + info',
        },
        externalAuthorNames: {
            type: 'array',
            description: 'Name(s) of the original partner article author(s)',
            items: {
                type: 'string',
            },
        },
        autopublishHistory: {
            type: 'array',
            description: 'Array of autopublish history. Each item is a history and status for one brand.',
            items: AssemblerAutopublishSubdocumentSchema,
        },
        guid: {
            type: 'string',
            description: 'Unique article ID parsed from feed.',
        },
        articleType: PartnerTypeEnum,
        articleTags: {
            type: 'array',
            description: 'Array of article tags (such as breaking-news, etc.)',
            items: {
                type: 'string',
            },
        },
        rawContentHash: {
            type: 'string',
            description: 'MD5 hash of the raw fetched content. Used to determine whether the content was updated.',
        },
    },
    required: ['_id', 'type', 'title', 'seoTitle', 'leadText', 'teaserType', 'content', 'canonicalUrl', 'partnerUuid', 'articleType'],
};
//# sourceMappingURL=assembler.content.model.schema.js.map